import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Input } from '@design-system/Input';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useUpdateDiscount,
  useRemoveDiscount,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type {
  DiscountsResponse,
  SMPPlanComponent,
  SMPSubscriptionDetail,
} from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { isSubscriptionCancelled, formatProductName, getComponentSupplyLabel } from './utils/smpHelpers';
import { ShimmerImage } from './components';
import { SMP_PATH } from '@/constants/paths';

type Step = 'view' | 'enter' | 'applying' | 'success' | 'remove-confirm';

const formatDiscountValue = (value: string): string => {
  if (!value) return '';
  return value.endsWith('.0') ? value.slice(0, -2) : value;
};

const formatDiscountLabel = (discount: DiscountsResponse): string => {
  const val = formatDiscountValue(discount.discountValue);
  return discount.discountType === 'percentage'
    ? `${val}% off subscription`
    : `$${val} off subscription`;
};

export const SMPDiscountCodePage = () => {
  const navigate = useNavigate();
  const {
    customerId,
    subscriptionDetail,
    productComponents,
    customerDiscount,
    showMaintenance,
    isLoading,
    isError,
  } = useSMPData({
    skip: { uiMessages: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const [step, setStep] = useState<Step>('enter');
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [discountRemoved, setDiscountRemoved] = useState(false);

  const discount = discountRemoved ? null : customerDiscount;
  const hasActiveDiscount = !!discount;

  const updateMutation = useUpdateDiscount();
  const removeMutation = useRemoveDiscount();

  const goBack = () => navigate(-1);

  // Once the discount query refetches and customerDiscount becomes available
  // while we're in the 'applying' shimmer state, transition to 'success'.
  useEffect(() => {
    if (step === 'applying' && customerDiscount) {
      setDiscountRemoved(false);
      setStep('success');
    }
  }, [step, customerDiscount]);

  const handleApply = () => {
    if (!code.trim() || !customerId) return;
    setErrorMessage('');
    updateMutation.mutate(
      { discountCode: code.trim(), customerId },
      {
        onSuccess: (data: unknown) => {
          const res = data as { error?: string };
          if (res?.error) {
            setErrorMessage(res.error);
          } else {
            setCode('');
            // Show shimmer while we wait for the discounts query to refetch.
            // The useEffect above will transition to 'success' once
            // customerDiscount is populated.
            setStep('applying');
          }
        },
        onError: () => {
          setErrorMessage('Invalid discount code. Please try again.');
        },
      },
    );
  };

  const handleRemove = () => {
    if (!customerId) return;
    removeMutation.mutate(customerId, {
      onSuccess: () => {
        setDiscountRemoved(true);
        setStep('enter');
      },
      onError: () => {
        setErrorMessage('Failed to remove discount code.');
        setStep('view');
      },
    });
  };

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-12 w-full max-w-sm" />
      </section>
    );
  }

  if (isError || !subscriptionDetail) {
    return <ErrorLoadingPage variant="light" errorType="error" hideBackground hideNavBar />;
  }

  if (isSubscriptionCancelled(subscriptionDetail.subscription_status)) {
    return (
      <section className="page-container flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" onReturnClick={goBack} className="md:-ml-9" />
        <h1 className="typography-h6 text-primary">Discount Code</h1>
        <p className="typography-body text-secondary">
          Discount codes are not available for cancelled subscriptions.
        </p>
      </section>
    );
  }

  const currentStep = hasActiveDiscount && step === 'enter' ? 'view' : step;
  const { line_items } = subscriptionDetail;

  return (
    <section className="page-container flex flex-col gap-8 pb-24">
      <NavBar returnButtonTitle="Back" onReturnClick={goBack} className="md:-ml-9" />

      {currentStep === 'enter' && <EnterCodeStep code={code} setCode={setCode} errorMessage={errorMessage} setErrorMessage={setErrorMessage} isApplying={updateMutation.isPending} onApply={handleApply} />}

      {currentStep === 'applying' && <ApplyingShimmer />}

      {currentStep === 'success' && <SuccessStep discount={discount} onRemove={() => setStep('remove-confirm')} onReturn={() => navigate(SMP_PATH, { replace: true })} />}

      {currentStep === 'view' && discount && (
        <ViewDiscountStep
          discount={discount}
          lineItems={line_items}
          productComponents={productComponents}
          subscriptionDetail={subscriptionDetail}
          onRemove={() => setStep('remove-confirm')}
        />
      )}

      {currentStep === 'remove-confirm' && discount && (
        <RemoveConfirmStep
          discountName={discount.discountCodeName}
          isRemoving={removeMutation.isPending}
          onConfirm={handleRemove}
          onCancel={() => setStep(hasActiveDiscount ? 'view' : 'enter')}
        />
      )}
    </section>
  );
};

/* ============ Sub-components ============ */

const CouponCard = ({ discount, onRemove }: { discount: DiscountsResponse; onRemove: () => void }) => (
  <div className="flex items-center justify-between rounded-[5px] border border-dashed border-[#009E82] bg-[#EAF2F1] px-4 py-3">
    <div className="flex flex-col gap-0">
      <span className="typography-body text-primary">{discount.discountCodeName}</span>
      <span className="typography-title text-[#009E82]">{formatDiscountLabel(discount)}</span>
    </div>
    <button className="typography-body text-error" onClick={onRemove}>Remove</button>
  </div>
);

const ApplyingShimmer = () => (
  <>
    <Skeleton className="h-12 w-80" />
    <Skeleton className="h-16 w-full max-w-xl rounded-lg" />
    <Skeleton className="h-12 w-full max-w-sm rounded-[20px]" />
  </>
);

const EnterCodeStep = ({ code, setCode, errorMessage, setErrorMessage, isApplying, onApply }: {
  code: string; setCode: (v: string) => void; errorMessage: string; setErrorMessage: (v: string) => void; isApplying: boolean; onApply: () => void;
}) => (
  <>
    <h1 className="typography-h6 text-primary">Discount Code</h1>
    <p className="typography-body text-secondary">Enter a discount code to apply to your subscription.</p>
    <div className="flex flex-col gap-2 max-w-sm">
      <label className="typography-caption1 text-secondary">Discount Code</label>
      <Input
        value={code}
        onChange={(e) => { setCode(e.target.value); setErrorMessage(''); }}
        placeholder="Enter code"
        disabled={isApplying}
        data-invalid={!!errorMessage || undefined}
        data-testid="smp-discount-input"
      />
      {errorMessage && <p className="typography-caption1 text-error">{errorMessage}</p>}
    </div>
    <div className="max-w-sm">
      <Button onClick={onApply} disabled={!code.trim() || isApplying} className="w-full" data-testid="smp-discount-applyBtn">
        {isApplying ? 'Applying...' : 'Apply'}
      </Button>
    </div>
  </>
);

const SuccessStep = ({ discount, onRemove, onReturn }: {
  discount: DiscountsResponse | null; onRemove: () => void; onReturn: () => void;
}) => (
  <>
    <h1 className="typography-h6 text-primary">Discount code{'\n'}successfully applied!</h1>
    {discount && <CouponCard discount={discount} onRemove={onRemove} />}
    <div className="max-w-sm">
      <Button onClick={onReturn} className="w-full" data-testid="smp-discount-returnBtn">Return to My Account</Button>
    </div>
  </>
);

type LineItem = { id: number; image: string; product_name: string; title?: string; price: number };

type DiscountDisplayRow = {
  key: string | number;
  image: string;
  title: string;
  supply: string;
};

const ViewDiscountStep = ({
  discount,
  lineItems,
  productComponents,
  subscriptionDetail,
  onRemove,
}: {
  discount: DiscountsResponse;
  lineItems: LineItem[];
  productComponents: SMPPlanComponent[];
  subscriptionDetail: SMPSubscriptionDetail;
  onRemove: () => void;
}) => {
  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const discountVal = parseFloat(discount.discountValue);
  const isPercent = discount.discountType === 'percentage';

  const applyDiscount = (priceCents: number) =>
    isPercent
      ? Math.round(priceCents * (1 - discountVal / 100))
      : Math.max(0, priceCents - discountVal * 100);

  const subTotalCents = lineItems.reduce((sum, item) => sum + item.price, 0);
  const discountedSubtotalCents = applyDiscount(subTotalCents);
  const discountAmountCents = subTotalCents - discountedSubtotalCents;

  const taxCents = subscriptionDetail.transaction_details?.tax ?? 0;
  const showTaxAmount = taxCents > 0;
  const totalCents = showTaxAmount ? discountedSubtotalCents + taxCents : discountedSubtotalCents;

  const displayRows: DiscountDisplayRow[] =
    productComponents.length > 0
      ? productComponents.map((c) => ({
          key: c.product_id,
          image: c.image,
          title: formatProductName(c.title),
          supply: getComponentSupplyLabel(c),
        }))
      : lineItems.map((item) => ({
          key: item.id,
          image: item.image,
          title: formatProductName(item.title || item.product_name),
          supply: '30-day supply',
        }));

  return (
    <>
      <h1 className="typography-h6 text-primary">Discount Code</h1>

      <div className="flex flex-col gap-3">
        <p className="typography-h2 text-primary pt-2 pb-3">Active Discount</p>
        <CouponCard discount={discount} onRemove={onRemove} />
      </div>

      <div className="mt-6 flex flex-col gap-6 rounded-[20px] bg-background py-6 pl-5 pr-9 shadow-none">
        {displayRows.map((row) => (
          <div key={row.key} className="flex items-stretch gap-5">
            <ShimmerImage src={row.image} alt={row.title} loading="lazy" containerClassName="relative size-[72px] shrink-0 self-center overflow-hidden rounded-[12px] bg-[#f5f9f9]" className="size-full object-cover" />
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
              <span className="typography-body text-primary">{row.title}</span>
              <span className="typography-caption2 text-secondary">{row.supply}</span>
            </div>
          </div>
        ))}

        <hr className="h-px w-full border-0 bg-black/20" />

        <div className="flex w-full flex-col gap-3 items-end">
          <PriceRow label="SUB-TOTAL" value={formatPrice(subTotalCents)} />
          <PriceRow label="DISCOUNT" value={`-${formatPrice(discountAmountCents)}`} />
          <PriceRow
            label="TAX"
            value={showTaxAmount ? formatPrice(taxCents) : 'Calculated at checkout'}
            isText={!showTaxAmount}
          />
          <hr className="h-px w-full max-w-xs border-0 bg-black/20" />
          <div className="flex w-full max-w-xs justify-between items-baseline">
            <span className="typography-body uppercase text-[#727484]">Total</span>
            <span className="typography-h3 text-primary">{formatPrice(totalCents)}/m</span>
          </div>
        </div>
      </div>
    </>
  );
};

const PriceRow = ({ label, value, isText }: { label: string; value: string; isText?: boolean }) => (
  <div className="flex w-full max-w-xs justify-between gap-4">
    <span className="typography-body uppercase text-[#727484]">{label}</span>
    <span className="typography-body text-primary text-end">{isText ? value : `${value}/m`}</span>
  </div>
);

const RemoveConfirmStep = ({ discountName, isRemoving, onConfirm, onCancel }: {
  discountName: string; isRemoving: boolean; onConfirm: () => void; onCancel: () => void;
}) => (
  <>
    <h1 className="typography-h6 text-primary">Remove Discount</h1>
    <div className="flex flex-col gap-2">
      <p className="typography-body text-primary font-semibold">
        Are you sure you want to remove the discount code {discountName}?
      </p>
      <p className="typography-body text-secondary">You may not be able to apply this offer again.</p>
    </div>
    <div className="flex flex-col gap-4 max-w-sm">
      <Button onClick={onConfirm} disabled={isRemoving} className="w-full" data-testid="smp-discount-confirmRemoveBtn">
        {isRemoving ? 'Removing...' : 'Yes, remove discount code'}
      </Button>
      <Button variant="secondary" onClick={onCancel} disabled={isRemoving} className="w-full" data-testid="smp-discount-cancelRemoveBtn">
        No, don&apos;t remove
      </Button>
    </div>
  </>
);
